import {
    GA4Offer,
    ClickoutLocation,
    OfferPageTemplateResponse,
    SanitisedOffer,
} from "@/types/offers";
import * as React from "react";
import styles from "./AdUnit.module.css";
import { handleOfferClick } from "@/components/OfferPage/OfferList";
import { CmsImage } from "@/components/common/CmsImage";
import { AD_UNIT_SKYSCRAPER_INDEX } from "../../../constants";
import type { Site } from "@/types/sites";
import { getGAAttributes } from "../../../utils/gaUtils";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import type { AdUnitResponse } from "@/types/adUnit";
import { useMemo, useRef, FormEvent } from "react";

export const adRowClass = "adRow";

const AdUnit = ({
    offerPage,
    site,
    altText,
    desiredHeight,
    maxWidth,
    responsive,
    sanitisedOffer,
    adUnit,
    isSkyScraper,
    perceivedIndex = 0,
    initialViewportWidth
}: {
    offerPage: OfferPageTemplateResponse;
    site: Site;
    altText: string;
    desiredHeight?: number;
    maxWidth?: number;
    responsive?: boolean;
    sanitisedOffer?: SanitisedOffer | null;
    adUnit: AdUnitResponse | null;
    isSkyScraper?: boolean;
    perceivedIndex?: number;
    initialViewportWidth?: number;
}): React.ReactElement => {
    const desktopImage = adUnit?.data?.attributes?.offerListImageDesktop;
    const mobileImage = adUnit?.data?.attributes?.offerListImageMobile;
    const skyScraperImage = adUnit?.data?.attributes?.skyscraperImage;
    const desktopImageMinWidth = adUnit?.data?.attributes?.offerListImageDesktopMinWidth;
    const mobileBreakpoint = offerPage?.data?.attributes?.OP?.mobileBreakpoint?? 768;
    const { isMobileWidth } = useBreakpoint(mobileBreakpoint,initialViewportWidth);
    const adUnitInlinePaddingMobile =
        adUnit?.data?.attributes?.inlinePaddingMobileValue;
    const adUnitInlinePaddingMobileUnit =
        adUnit?.data?.attributes?.inlinePaddingMobileUnit;

    const imageConfig = useRef((() => {
        const validAdUnitMobilePaddingUnit =
            adUnitInlinePaddingMobileUnit === "percent" ? "%" : adUnitInlinePaddingMobileUnit;

        const adUnitMobilePadding = adUnitInlinePaddingMobile && validAdUnitMobilePaddingUnit
            ? `${adUnitInlinePaddingMobile}${validAdUnitMobilePaddingUnit}`
            : "0";

        const defaultImage = isSkyScraper
            ? skyScraperImage || null
            : mobileImage || desktopImage || null;

        return {
            desktopImage,
            mobileImage,
            skyScraperImage,
            desktopImageMinWidth,
            defaultImage,
            isSkyScraper,
            adUnitMobilePadding
        };
    })());
    const formOnSubmitTask = (e: FormEvent<HTMLFormElement>) => {
        offerToUse && handleOfferClick(
            e,
            offerToUse,
            indexToUse,
            offerPage,
            site,
            offerToUse.epcId || '',
            ga4Offer,
            isSkyScraper
                ? ClickoutLocation.Skyscraper
                : ClickoutLocation.AdRow
        );
    }


    const imageToUse = useMemo(() => {
        const { skyScraperImage, desktopImage, mobileImage, desktopImageMinWidth, defaultImage, isSkyScraper } = imageConfig.current;
        
        if (isSkyScraper) return skyScraperImage || null;
        
        // If we have both mobile and desktop images and a min width
        if (desktopImage && mobileImage && desktopImageMinWidth) {
            return isMobileWidth ? mobileImage : desktopImage;
        }
        
        return defaultImage;
    }, [isMobileWidth]);



    const inlineStyles = useMemo(() => {
        if (imageConfig.current.isSkyScraper || imageToUse === imageConfig.current.desktopImage) {
            return {};
        }
        return { paddingInline: imageConfig.current.adUnitMobilePadding };
    }, [imageToUse]);

    /**
     *
     * In ACD-19048 we began supporting multiple "Site of the months" and renamed them to "Ad unit"
     * on the same page.
     *
     * For now, we still support the old field on an offer page to add the site of the month too.
     * This means if we're adding an old site of the month, the offer is at `offerPage.sanitisedSiteOfTheMonth`
     *
     * If we're using the new field, we pass in the sanitisedOffer to use as a prop
     *
     *  */
    const offerToUse = sanitisedOffer || offerPage.sanitisedSiteOfTheMonth;
    const indexToUse = perceivedIndex
        ? perceivedIndex + 1000
        : AD_UNIT_SKYSCRAPER_INDEX;

    const ga4Offer: GA4Offer = getGAAttributes(offerToUse, indexToUse);
    const formAttributes = {
        subId:sanitisedOffer?.subId || '',
        epcId:sanitisedOffer?.epcId || '',
        merchant:sanitisedOffer?.merchant?.data?.attributes?.name || ''
    }
    return (
        <>
            {imageToUse && offerPage && offerToUse?.offerLink && (
                <form onSubmit={(e) => formOnSubmitTask(e)}
                id={'adrow-' + sanitisedOffer?.id}
                className="adrow-form"
                data-attr={JSON.stringify(formAttributes)}>
                    <button
                        className={[
                            styles.adUnitImage,
                             adRowClass
                        ].join(" ")}
                        style={inlineStyles}
                        data-ga-attributes={
                            JSON.stringify(ga4Offer)
                        }
                        aria-label={`Click to view offer from ${offerToUse?.merchant?.data?.attributes?.name ?? 'merchant'}`}
                    >
                        <CmsImage
                            cmsMedia={imageToUse}
                            desiredHeight={desiredHeight}
                            maxWidth={maxWidth}
                            defaultAlt={altText}
                            responsive={responsive}
                            priority={perceivedIndex < 4}
                            loading={perceivedIndex < 4 ? "eager" : "lazy"}
                        />
                    </button>
                </form>
            )}
        </>
    );
};

export default AdUnit;
